import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { FocusScope } from "@/components/ui/focus-scope";

/**
 * Dialog — centred modal over a scrim.
 *
 * Hand-rolled rather than Radix: the dashboard has no `@radix-ui/react-dialog`
 * dependency and every other overlay here is built on `FocusScope`, which
 * already supplies the tab trap, scroll lock and focus restore. Esc is handled
 * here because this component owns the topmost layer.
 *
 * Per the DS, `shadow-*` is an overlay-only exception — elevation is borders
 * everywhere else.
 */

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Id of the element naming the dialog — pass the `<DialogTitle>`'s id. */
  labelledBy?: string;
  className?: string;
  overlayTestId?: string;
  "data-testid"?: string;
}

export function Dialog({
  open,
  onClose,
  children,
  labelledBy,
  className,
  overlayTestId,
  "data-testid": testId,
}: DialogProps): React.ReactElement | null {
  const pressedOnScrim = React.useRef(false);
  const releasedOnScrim = React.useRef(false);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <FocusScope
      // z-[2000] matches ConfirmDialog. The sticky topbar is z-100 and the
      // impersonation banner z-999, so anything lower paints *under* the
      // chrome and leaves the nav clickable behind a supposedly modal dialog.
      // `.pf-scrim` — the design's overlay: its own backdrop token, no blur.
      // The blur was ours; it softened the page behind every dialog into
      // something the rest of the platform never does.
      className="pf-scrim"
      // Close only when the gesture both started and ended on the scrim.
      // A click's target is the common ancestor of its mousedown and mouseup,
      // so a drag that crosses the panel edge in *either* direction reports the
      // overlay as the target — which would otherwise dismiss the dialog
      // mid-selection and destroy the very text being copied. Both ends are
      // recorded because checking only the press left the inward drag broken.
      onMouseDown={(e) => {
        pressedOnScrim.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        releasedOnScrim.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // `detail === 0` is a click with no pointer behind it — Enter or Space
        // on a focused control inside the panel. Those bubble here carrying the
        // *previous* gesture's refs, which closed the dialog mid-action and made
        // the disclosure toggle unusable from the keyboard.
        const fromPointer = e.detail > 0;
        const onScrim = pressedOnScrim.current && releasedOnScrim.current;
        pressedOnScrim.current = false;
        releasedOnScrim.current = false;
        if (fromPointer && onScrim) onClose();
      }}
      data-testid={overlayTestId}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        data-testid={testId}
        // `.pf-modal` carries the design's panel: 480px, overlay background,
        // emphasis border, 2xlarge radius, floating shadow and the pfIn entrance.
        // The padding lives on the header/body/footer parts rather than the
        // panel, which is what lets the footer draw its own band.
        className={cn("pf-modal relative my-auto", className)}
      >
        {children}
      </div>
    </FocusScope>,
    document.body,
  );
}

export function DialogHeader({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return <div className={cn("pf-modal__h flex-col gap-1 pr-10", className)}>{children}</div>;
}

export function DialogTitle({
  children,
  className,
  id,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>): React.ReactElement {
  return (
    <h2 id={id} className={cn("pf-modal__t", className)} {...props}>
      {children}
    </h2>
  );
}

export function DialogDescription({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>): React.ReactElement {
  return (
    <p className={cn("text-sm text-muted-foreground", className)} {...props}>
      {children}
    </p>
  );
}

export function DialogFooter({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn("pf-modal__f items-center", className)}>{children}</div>
  );
}

/**
 * Body of a dialog. The design gives the body its own padding and muted colour,
 * and it is what sits between the header and the footer band — without it, a
 * caller's content butts straight against the panel edge now that the panel
 * itself no longer carries padding.
 */
export function DialogBody({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return <div className={cn("pf-modal__b", className)}>{children}</div>;
}

export function DialogCloseButton({
  onClick,
  "data-testid": testId,
}: {
  onClick: () => void;
  "data-testid"?: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      data-testid={testId}
      className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <X className="size-4" strokeWidth={1.5} />
    </button>
  );
}
